import React, { useState, useEffect, useCallback } from "react";
import {
  CheckCircle,
  XCircle,
  Eye,
  Download,
  Loader2,
  Clock,
  Package,
  AlertTriangle,
  ChevronLeft,
  X,
  TrendingUp,
} from "lucide-react";
import { db } from "../services/firebase";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import Layout from "../component/Layout";
import "./Productsregistrationreq.css";

const REJECTION_REASONS = [
  "Incomplete product information",
  "Poor quality product images",
  "Product does not meet category standards",
  "Invalid purity / specification details",
  "Duplicate registration detected",
  "Pricing information is incorrect",
  "Missing required certifications",
  "Product violates platform policies",
  "Seller account not verified",
  "Other (see additional notes)",
];

// const PAGE_SIZE = 9;

const ProductsRegistrationReq = () => {
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [activeTab, setActiveTab] = useState("pending");

  // Pagination
  // const [currentPage, setCurrentPage] = useState(1);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [modalRegistration, setModalRegistration] = useState(null);
  const [activeModalImageIndex, setActiveModalImageIndex] = useState(0);

  // Modal rejection reason
  const [modalRejectReason, setModalRejectReason] = useState("");
  const [modalRejectNotes, setModalRejectNotes] = useState("");

  // Toast notification
  const [toast, setToast] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const isPending = useCallback((reg) => {
    if (!reg.status) return false;
    const s = reg.status.trim().toLowerCase();
    return s === "pending_approval" || s === "pending";
  }, []);

  useEffect(() => {
    const fetchAllRegistrations = async () => {
      try {
        const querySnapshot = await getDocs(
          collection(db, "ProductRegistrations")
        );
        let allRegistrations = [];
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.registrations && Array.isArray(data.registrations)) {
            const withSeller = data.registrations.map((reg) => ({
              ...reg,
              sellerId: docSnap.id,
            }));
            allRegistrations.push(...withSeller);
          }
        });
        allRegistrations.sort((a, b) => {
          const tA = a.requestTimestamp?.toDate?.() || new Date(0);
          const tB = b.requestTimestamp?.toDate?.() || new Date(0);
          return tB - tA;
        });
        setRegistrations(allRegistrations);
      } catch (error) {
        console.error("Error fetching registrations:", error);
        showToast("Failed to load registrations", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchAllRegistrations();
  }, []);

  useEffect(() => {
    if (activeTab === "pending") {
      setFilteredRegistrations(registrations.filter(isPending));
    } else if (activeTab === "approved") {
      setFilteredRegistrations(
        registrations.filter(
          (reg) => reg.status?.trim().toLowerCase() === "approved"
        )
      );
    } else if (activeTab === "rejected") {
      setFilteredRegistrations(
        registrations.filter(
          (reg) => reg.status?.trim().toLowerCase() === "rejected"
        )
      );
    }
    // setCurrentPage(1); // reset to first page on tab change
  }, [registrations, activeTab, isPending]);

  const updateRegistrationInFirestore = async (registration, updateData) => {
    const docRef = doc(db, "ProductRegistrations", registration.sellerId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) throw new Error("Document not found");
    const data = docSnap.data();
    const updatedRegistrations = data.registrations.map((reg) =>
      reg.registrationId === registration.registrationId
        ? { ...reg, ...updateData, lastUpdated: new Date() }
        : reg
    );
    await updateDoc(docRef, {
      registrations: updatedRegistrations,
      lastUpdated: new Date(),
    });
    setRegistrations((prev) =>
      prev.map((reg) =>
        reg.registrationId === registration.registrationId &&
        reg.sellerId === registration.sellerId
          ? { ...reg, ...updateData, lastUpdated: new Date() }
          : reg
      )
    );
  };

  const handleApprove = async (registration) => {
    setActionLoading(true);
    try {
      await updateRegistrationInFirestore(registration, {
        approved: true,
        status: "approved",
      });
      closeModal();
      // Update selectedRegistration if in detail view
      if (selectedRegistration?.registrationId === registration.registrationId) {
        setSelectedRegistration((prev) => ({ ...prev, approved: true, status: "approved" }));
      }
      showToast("Registration approved successfully! ✓", "success");
    } catch (error) {
      console.error("Error approving:", error);
      showToast("Failed to approve registration", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (registration, reason, notes) => {
    if (!reason) {
      showToast("Please select a rejection reason", "error");
      return;
    }
    setActionLoading(true);
    const comment = notes ? `${reason} — ${notes}` : reason;
    try {
      await updateRegistrationInFirestore(registration, {
        approved: false,
        status: "rejected",
        comment,
        rejectionReason: reason,
      });
      closeModal();
      if (selectedRegistration?.registrationId === registration.registrationId) {
        setSelectedRegistration((prev) => ({
          ...prev,
          approved: false,
          status: "rejected",
          comment,
          rejectionReason: reason,
        }));
      }
      showToast("Registration rejected.", "warning");
    } catch (error) {
      console.error("Error rejecting:", error);
      showToast("Failed to reject registration", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const openApproveModal = (registration) => {
    setModalRegistration(registration);
    setModalAction("approve");
    setModalRejectReason("");
    setModalRejectNotes("");
    setActiveModalImageIndex(0);
    setIsModalOpen(true);
  };

  const openRejectModal = (registration) => {
    setModalRegistration(registration);
    setModalAction("reject");
    setModalRejectReason("");
    setModalRejectNotes("");
    setActiveModalImageIndex(0);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalRegistration(null);
    setModalAction(null);
    setModalRejectReason("");
    setModalRejectNotes("");
  };

  const viewRegistrationDetails = (registration) => {
    setSelectedRegistration(registration);
    setViewMode("detail");
  };

  const downloadImage = (imageUrl, productName) => {
    if (!imageUrl) return;
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = `${productName}_${selectedRegistration?.registrationId}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const kpis = {
    pending: registrations.filter(isPending).length,
    approved: registrations.filter(
      (r) => r.status?.trim().toLowerCase() === "approved"
    ).length,
    rejected: registrations.filter(
      (r) => r.status?.trim().toLowerCase() === "rejected"
    ).length,
    total: registrations.length,
  };

  const getStatusBadge = (status) => {
    const s = status?.trim().toLowerCase();
    if (s === "approved")
      return <span className="prr-badge prr-badge--approved"><CheckCircle size={11} /> Approved</span>;
    if (s === "rejected")
      return <span className="prr-badge prr-badge--rejected"><XCircle size={11} /> Rejected</span>;
    return <span className="prr-badge prr-badge--pending"><Clock size={11} /> Pending</span>;
  };

  // ─── Modal Renderer ───────────────────────────────────────────────────────
  const renderModal = () => {
    const reg = modalRegistration;
    const productValues = Object.values(reg.products || {});
    const productKeys = Object.keys(reg.products || {});
    const currentProduct = productValues[activeModalImageIndex];

    return (
      <div
        className="prr-modal-overlay"
        onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
      >
        <div className="prr-modal" onClick={(e) => e.stopPropagation()}>
          {/* Modal Header */}
          <div className={`prr-modal-header ${modalAction === "approve" ? "prr-modal-header--approve" : "prr-modal-header--reject"}`}>
            <div className="prr-modal-header-left">
              {modalAction === "approve" ? <CheckCircle size={20} /> : <XCircle size={20} />}
              <span>{modalAction === "approve" ? "Approve Registration" : "Reject Registration"}</span>
            </div>
            <button className="prr-modal-close" onClick={closeModal}><X size={20} /></button>
          </div>

          {/* Modal Body */}
          <div className="prr-modal-body">
            {/* Image Gallery */}
            {productValues.length > 0 && (
              <div className="prr-modal-gallery">
                <div className="prr-modal-main-img">
                  {currentProduct?.image ? (
                    <img src={currentProduct.image} alt={productKeys[activeModalImageIndex]} />
                  ) : (
                    <div className="prr-no-img-lg"><Package size={40} opacity={0.3} /><span>No Image</span></div>
                  )}
                  <div className="prr-modal-img-label">{productKeys[activeModalImageIndex]}</div>
                </div>
                {productValues.length > 1 && (
                  <div className="prr-modal-thumbs">
                    {productValues.map((p, idx) =>
                      p.image ? (
                        <img
                          key={idx}
                          src={p.image}
                          alt={productKeys[idx]}
                          className={idx === activeModalImageIndex ? "prr-thumb-active" : ""}
                          onClick={() => setActiveModalImageIndex(idx)}
                          title={productKeys[idx]}
                        />
                      ) : null
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Registration Info */}
            <div className="prr-modal-info-grid">
              <div className="prr-modal-info-item">
                <span className="prr-info-label">Registration ID</span>
                <span className="prr-info-value prr-mono">{reg.registrationId}</span>
              </div>
              <div className="prr-modal-info-item">
                <span className="prr-info-label">Seller ID</span>
                <span className="prr-info-value prr-mono">{reg.sellerId}</span>
              </div>
              <div className="prr-modal-info-item">
                <span className="prr-info-label">Products</span>
                <span className="prr-info-value">{Object.keys(reg.products || {}).join(", ")}</span>
              </div>
              <div className="prr-modal-info-item">
                <span className="prr-info-label">Requested On</span>
                <span className="prr-info-value">{reg.requestTimestamp?.toDate?.().toLocaleString() || "N/A"}</span>
              </div>
            </div>

            {/* Reject Reason Dropdown */}
            {modalAction === "reject" && (
              <div className="prr-modal-reject-section">
                <label className="prr-field-label" htmlFor="modal-reject-reason">
                  <AlertTriangle size={14} /> Rejection Reason <span className="prr-required">*</span>
                </label>
                <select
                  id="modal-reject-reason"
                  className="prr-select"
                  value={modalRejectReason}
                  onChange={(e) => setModalRejectReason(e.target.value)}
                >
                  <option value="">— Select a reason —</option>
                  {REJECTION_REASONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>

                <label className="prr-field-label" htmlFor="modal-reject-notes" style={{ marginTop: "14px" }}>
                  Additional Notes <span className="prr-optional">(optional)</span>
                </label>
                <textarea
                  id="modal-reject-notes"
                  className="prr-textarea"
                  placeholder="Add any additional context for the seller…"
                  value={modalRejectNotes}
                  onChange={(e) => setModalRejectNotes(e.target.value)}
                  rows={3}
                />
              </div>
            )}

            {/* Approve Confirmation */}
            {modalAction === "approve" && (
              <div className="prr-modal-approve-confirm">
                <CheckCircle size={32} className="prr-confirm-icon" />
                <p>
                  You are about to <strong>approve</strong> this registration.
                  The seller will be notified and can start listing products immediately.
                </p>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="prr-modal-footer">
            <button className="prr-btn prr-btn--ghost" onClick={closeModal} disabled={actionLoading}>
              Cancel
            </button>
            {modalAction === "approve" ? (
              <button
                className="prr-btn prr-btn--approve"
                onClick={() => handleApprove(reg)}
                disabled={actionLoading}
              >
                {actionLoading ? <Loader2 size={16} className="prr-spin" /> : <CheckCircle size={16} />}
                {actionLoading ? "Approving…" : "Confirm Approve"}
              </button>
            ) : (
              <button
                className="prr-btn prr-btn--reject"
                onClick={() => handleReject(reg, modalRejectReason, modalRejectNotes)}
                disabled={actionLoading || !modalRejectReason}
              >
                {actionLoading ? <Loader2 size={16} className="prr-spin" /> : <XCircle size={16} />}
                {actionLoading ? "Rejecting…" : "Confirm Reject"}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── Loading State ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Layout pageTitle="Product Registration Requests">
        <div className="prr-loading">
          <Loader2 size={40} className="prr-spin" />
          <p>Loading registration requests…</p>
        </div>
      </Layout>
    );
  }

  // ─── Detail View ──────────────────────────────────────────────────────────
  if (viewMode === "detail" && selectedRegistration) {
    const reg = selectedRegistration;
    const productEntries = Object.entries(reg.products || {});
    return (
      <Layout pageTitle="Registration Details">
        <div className="prr-root">
          {toast && <div className={`prr-toast prr-toast--${toast.type}`}>{toast.message}</div>}

          <button className="prr-back-btn" onClick={() => setViewMode("list")}>
            <ChevronLeft size={18} /> Back to List
          </button>

          <div className="prr-detail-header">
            <div>
              <h2 className="prr-page-title">Registration Details</h2>
              <p className="prr-page-subtitle">ID: <code className="prr-mono">{reg.registrationId}</code></p>
            </div>
            <div>{getStatusBadge(reg.status)}</div>
          </div>

          {/* Info Grid */}
          <div className="prr-detail-card">
            <h3 className="prr-section-title">Basic Information</h3>
            <div className="prr-info-grid">
              {[
                { label: "Seller ID", value: reg.sellerId },
                { label: "Registration ID", value: reg.registrationId },
                { label: "Segment", value: productEntries[0]?.[1]?.segment || reg.segment || "—" },
                { label: "Category", value: productEntries[0]?.[1]?.category || reg.category || "—" },
                { label: "Source", value: productEntries[0]?.[1]?.productSource || "—" },
                { label: "Requested", value: reg.requestTimestamp?.toDate?.().toLocaleString() || "N/A" },
              ].map(({ label, value }) => (
                <div className="prr-info-item" key={label}>
                  <span className="prr-info-label">{label}</span>
                  <span className="prr-info-value">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Product Cards */}
          <div className="prr-detail-card">
            <h3 className="prr-section-title">Products ({productEntries.length})</h3>
            <div className="prr-products-grid">
              {productEntries.map(([name, product]) => (
                <div className="prr-product-tile" key={name}>
                  {product.image ? (
                    <div className="prr-product-tile-img-wrap">
                      <img src={product.image} alt={name} />
                      <button
                        className="prr-download-overlay"
                        onClick={() => downloadImage(product.image, name)}
                        title="Download image"
                      >
                        <Download size={15} />
                      </button>
                    </div>
                  ) : (
                    <div className="prr-product-tile-no-img"><Package size={30} opacity={0.3} /></div>
                  )}
                  <div className="prr-product-tile-body">
                    <h4>{name}</h4>
                    <div className="prr-product-tile-specs">
                      {[
                        ["Purity", product.purity ? `${product.purity}%` : "—"],
                        ["Style", product.selectedStyleType || "—"],
                        ["Specification", product.specification || "—"],
                        ["Wastage", product.wastage ? `${product.wastage}%` : "—"],
                        ["Set MC", product.setMC ? `${product.setMC}%` : "—"],
                        ["Net Gram MC", product.netGramMC ? `${product.netGramMC}%` : "—"],
                        ["Spec MC", product.specificationMC ? `${product.specificationMC}%` : "—"],
                        ["Gram Rate", product.specificationGramRate ? `₹${product.specificationGramRate}` : "—"],
                      ].map(([k, v]) => (
                        <div className="prr-spec-row" key={k}>
                          <span className="prr-spec-key">{k}</span>
                          <span className="prr-spec-val">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Approval Actions */}
          {isPending(reg) && (
            <div className="prr-detail-card prr-actions-card">
              <h3 className="prr-section-title">Review Actions</h3>
              <p className="prr-actions-hint">Select an action to approve or reject this registration. You will be prompted to confirm.</p>
              <div className="prr-action-row">
                <button className="prr-btn prr-btn--approve prr-btn--lg" onClick={() => openApproveModal(reg)}>
                  <CheckCircle size={18} /> Approve Registration
                </button>
                <button className="prr-btn prr-btn--reject prr-btn--lg" onClick={() => openRejectModal(reg)}>
                  <XCircle size={18} /> Reject Registration
                </button>
              </div>
            </div>
          )}

          {/* Rejection Comment */}
          {reg.comment && (
            <div className="prr-detail-card prr-comment-card">
              <h3 className="prr-section-title">Admin Decision Notes</h3>
              <p className="prr-comment-text">{reg.comment}</p>
            </div>
          )}
        </div>

        {isModalOpen && modalRegistration && renderModal()}
      </Layout>
    );
  }

  // ─── List View ────────────────────────────────────────────────────────────
  return (
    <Layout pageTitle="Product Registration Requests">
      <div className="prr-root">
        {/* Toast */}
        {toast && <div className={`prr-toast prr-toast--${toast.type}`}>{toast.message}</div>}

        {/* Page Header */}
        <div className="prr-page-header">
          <div>
            <h1 className="prr-page-title">Product Registration Requests</h1>
            <p className="prr-page-subtitle">Review and act on seller product registration submissions.</p>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="prr-kpis">
          {[
            { label: "Pending", value: kpis.pending, icon: <Clock size={22} />, mod: "pending" },
            { label: "Approved", value: kpis.approved, icon: <CheckCircle size={22} />, mod: "approved" },
            { label: "Rejected", value: kpis.rejected, icon: <XCircle size={22} />, mod: "rejected" },
            { label: "Total", value: kpis.total, icon: <Package size={22} />, mod: "total" },
          ].map(({ label, value, icon, mod }) => (
            <div className={`prr-kpi prr-kpi--${mod}`} key={label}>
              <div className="prr-kpi-icon">{icon}</div>
              <div>
                <div className="prr-kpi-value">{value}</div>
                <div className="prr-kpi-label">{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="prr-tabs">
          {["pending", "approved", "rejected"].map((tab) => (
            <button
              key={tab}
              className={`prr-tab ${activeTab === tab ? "prr-tab--active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === "pending" && <Clock size={14} />}
              {tab === "approved" && <CheckCircle size={14} />}
              {tab === "rejected" && <XCircle size={14} />}
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              <span className="prr-tab-count">
                {tab === "pending" ? kpis.pending : tab === "approved" ? kpis.approved : kpis.rejected}
              </span>
            </button>
          ))}
        </div>

        {/* Registration Cards */}
        {filteredRegistrations.length === 0 ? (
          <div className="prr-empty">
            <TrendingUp size={48} />
            <p>No {activeTab} registrations found.</p>
          </div>
        ) : (
          <div className="prr-grid">
            {filteredRegistrations.map((reg, index) => {
              const productValues = Object.values(reg.products || {});
              const productKeys = Object.keys(reg.products || {});
              const mainImage = productValues[0]?.image;

              return (
                <div key={`${reg.sellerId}_${reg.registrationId}`} className="prr-card">
                  {/* Image Section */}
                  <div className="prr-card-img-wrap">
                    {mainImage ? (
                      <img src={mainImage} alt={productKeys[0]} className="prr-card-img" />
                    ) : (
                      <div className="prr-card-no-img">
                        <Package size={36} />
                        <span>No Image</span>
                      </div>
                    )}
                    <div className="prr-card-badge-overlay">{getStatusBadge(reg.status)}</div>
                    {/* Thumbnail strip */}
                    {productValues.length > 1 && (
                      <div className="prr-card-thumbs">
                        {productValues.slice(0, 4).map((p, idx) =>
                          p.image ? (
                            <img key={idx} src={p.image} alt={productKeys[idx]} title={productKeys[idx]} />
                          ) : null
                        )}
                        {productValues.length > 4 && (
                          <div className="prr-card-more-imgs">+{productValues.length - 4}</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Content Section */}
                  <div className="prr-card-body">
                    <div className="prr-card-top">
                      <h3 className="prr-card-title">Registration #{index + 1}</h3>
                      <span className="prr-card-date">
                        {reg.requestTimestamp?.toDate?.().toLocaleDateString("en-IN", {
                          day: "2-digit", month: "short", year: "numeric",
                        }) || "N/A"}
                      </span>
                    </div>

                    <div className="prr-card-meta">
                      <div className="prr-card-meta-row">
                        <span className="prr-meta-label">Seller</span>
                        <span className="prr-meta-value prr-mono" title={reg.sellerId}>
                          {reg.sellerId?.slice(0, 14)}…
                        </span>
                      </div>
                      <div className="prr-card-meta-row">
                        <span className="prr-meta-label">Category</span>
                        <span className="prr-meta-value">{reg.category || productValues[0]?.category || "—"}</span>
                      </div>
                      <div className="prr-card-meta-row">
                        <span className="prr-meta-label">Products</span>
                        <span className="prr-meta-value">
                          {productKeys.slice(0, 2).join(", ")}
                          {productKeys.length > 2 ? ` +${productKeys.length - 2} more` : ""}
                        </span>
                      </div>
                    </div>

                    {/* Rejection note on rejected cards */}
                    {reg.status?.trim().toLowerCase() === "rejected" && reg.comment && (
                      <div className="prr-card-rejection-note">
                        <AlertTriangle size={12} />
                        <span>{reg.comment}</span>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="prr-card-actions">
                      <button className="prr-btn prr-btn--ghost prr-btn--sm" onClick={() => viewRegistrationDetails(reg)}>
                        <Eye size={14} /> Details
                      </button>
                      {isPending(reg) && (
                        <>
                          <button className="prr-btn prr-btn--approve prr-btn--sm" onClick={() => openApproveModal(reg)}>
                            <CheckCircle size={14} /> Approve
                          </button>
                          <button className="prr-btn prr-btn--reject prr-btn--sm" onClick={() => openRejectModal(reg)}>
                            <XCircle size={14} /> Reject
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && modalRegistration && renderModal()}
    </Layout>
  );
};

export default ProductsRegistrationReq;
